import { inject, Injectable, type Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { doc, docData, Firestore } from '@angular/fire/firestore';
import { catchError, map, of, switchMap } from 'rxjs';
import { AuthService } from './auth.service';
import {
    CLIENT_ERROR_FLAG_PATH,
    type ClientErrorFlagStatus,
    type ClientErrorKind,
    type ClientErrorLoggingFlag,
    type ClientErrorWho,
} from './types/client-error';
import { stableBucket } from './client-error-redact';
import { typeConverter } from './util';

type FlagState = {
    loaded: boolean;
    flag: ClientErrorLoggingFlag | null;
};

/** Sentinel in `allowedCustomerIds` meaning every customer is eligible. */
const ALL_CUSTOMERS = '*';

/**
 * Reads the runtime flag that decides who publishes client errors.
 *
 * The flag lives in a Firestore document read as a live snapshot, so flipping it propagates to every
 * open browser within seconds and without a redeploy. That makes it the fastest rollback lever
 * available for this feature.
 *
 * Fails closed: a missing, unreadable or malformed flag denies logging rather than allowing it.
 */
@Injectable({
    providedIn: 'root',
})
export class ClientErrorFlagService {
    private readonly firestore = inject(Firestore);
    private readonly authService = inject(AuthService);

    private readonly flagDoc = doc(this.firestore, CLIENT_ERROR_FLAG_PATH).withConverter(typeConverter<ClientErrorLoggingFlag>());

    /**
     * Re-subscribed on every auth change.
     *
     * Reading the flag requires an authenticated user, so a subscription opened while signed out would
     * error and stay terminated. Keying it on the user makes it recover once sign-in completes.
     */
    private readonly state: Signal<FlagState> = toSignal(
        this.authService.user$.pipe(
            switchMap(user => {
                if (!user) {
                    return of<FlagState>({ loaded: true, flag: null });
                }
                return docData(this.flagDoc).pipe(
                    map(flag => ({ loaded: true, flag: flag ?? null }) satisfies FlagState),
                    catchError(() => of<FlagState>({ loaded: true, flag: null })),
                );
            }),
        ),
        { initialValue: { loaded: false, flag: null } satisfies FlagState },
    );

    /**
     * Decides whether the given user may publish an event of the given kind.
     *
     * @param who - Identity attached to the buffered event.
     * @param kind - What the event describes. Kinds can be disabled individually, so runtime and crash
     *     reporting can be pulled without also blinding the upload diagnostics.
     * @returns `unknown` while the flag is still loading, so the caller can retry rather than discard.
     */
    status(who: ClientErrorWho, kind: ClientErrorKind): ClientErrorFlagStatus {
        const { loaded, flag } = this.state();

        if (!loaded) {
            return 'unknown';
        }
        if (!flag?.enabled) {
            return 'denied';
        }
        // Absent means enabled: the live flag document predates per-kind switches, and the allowlists
        // below already decide who publishes anything at all.
        if (flag.kinds?.[kind] === false) {
            return 'denied';
        }
        if (who.isAdmin && flag.includeAdmins) {
            return 'allowed';
        }
        if (who.email && flag.allowedEmails?.includes(who.email)) {
            return 'allowed';
        }
        if (flag.allowedCustomerIds?.includes(ALL_CUSTOMERS)) {
            return 'allowed';
        }
        if (who.customerId && flag.allowedCustomerIds?.includes(who.customerId)) {
            return 'allowed';
        }
        // Bucketed on the uid rather than per event, so a user is consistently in or out of the
        // sampled cohort for the whole of their session.
        if (flag.sampleRate > 0 && stableBucket(who.uid) < flag.sampleRate) {
            return 'allowed';
        }

        return 'denied';
    }
}
